// Pure parser of `git diff` unified patches (GET /api/atelier/sessions/:id/diff → patch) into
// files → hunks → lines, for the Atelier's diff viewer. No DOM. Tested in test/ui-atelier.test.js.
//
// Robust on hostile or cut input: hunk line counts decide where a hunk ends (so a removed line
// that reads "-- x" is never taken for a "--- a/…" header), quoted paths with C escapes (git's
// core.quotePath, octal UTF-8 bytes) are decoded, and a patch cut at 400 KB just ends the last file.

/** Parsing stops after this many lines (the server caps the patch at 400 KB anyway). */
export const MAX_PATCH_LINES = 60_000;

const HUNK_RE = /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@ ?(.*)$/;
const DECODER = typeof TextDecoder === 'function' ? new TextDecoder('utf-8', { fatal: false }) : null;

/**
 * Decodes a git path token: `"a/caf\303\251 x.js"` → `a/café x.js`; unquoted tokens are returned
 * as they are.
 * @param {string} token
 * @returns {string}
 */
export function unquotePath(token) {
  const s = String(token ?? '');
  if (s.length < 2 || s[0] !== '"' || s[s.length - 1] !== '"') return s;
  const bytes = [];
  const pushText = (text) => { for (const b of new TextEncoder().encode(text)) bytes.push(b); };
  const simple = { n: '\n', t: '\t', r: '\r', b: '\b', f: '\f', v: '\v', a: '\x07', '"': '"', '\\': '\\' };
  for (let i = 1; i < s.length - 1; i++) {
    const c = s[i];
    if (c !== '\\') { pushText(c); continue; }
    const next = s[i + 1];
    if (/[0-7]/.test(next || '')) {
      const m = /^[0-7]{1,3}/.exec(s.slice(i + 1, i + 4));
      bytes.push(parseInt(m[0], 8) & 0xff);
      i += m[0].length;
    } else if (next !== undefined && Object.prototype.hasOwnProperty.call(simple, next)) {
      pushText(simple[next]);
      i += 1;
    } else {
      pushText(c);
    }
  }
  return DECODER ? DECODER.decode(new Uint8Array(bytes)) : String.fromCharCode(...bytes);
}

/** Removes the `a/` or `b/` prefix of a diff path; `/dev/null` → null. */
function stripPrefix(p) {
  const s = unquotePath(String(p ?? '').replace(/\t.*$/, '').trim());
  if (!s || s === '/dev/null') return null;
  return /^[ab]\//.test(s) ? s.slice(2) : s;
}

/**
 * The two paths of a `diff --git a/X b/Y` line (quoted or not). For unquoted paths with spaces,
 * the split is the one that gives identical halves (the usual case), else the last " b/".
 * @param {string} line
 * @returns {{oldPath: string|null, newPath: string|null}}
 */
export function parseGitHeader(line) {
  const rest = String(line).replace(/^diff --git /, '');
  const quoted = /^("(?:[^"\\]|\\.)*"|\S+) ("(?:[^"\\]|\\.)*"|\S+)$/.exec(rest);
  if (quoted && (quoted[1].startsWith('"') || quoted[2].startsWith('"'))) {
    return { oldPath: stripPrefix(quoted[1]), newPath: stripPrefix(quoted[2]) };
  }
  if (rest.startsWith('a/') && (rest.length - 5) % 2 === 0) {
    const len = (rest.length - 5) / 2;
    const a = rest.slice(2, 2 + len);
    if (rest.slice(2 + len, 5 + len) === ' b/' && rest.slice(5 + len) === a) return { oldPath: a, newPath: a };
  }
  const idx = rest.lastIndexOf(' b/');
  if (rest.startsWith('a/') && idx > 1) return { oldPath: rest.slice(2, idx), newPath: rest.slice(idx + 3) };
  return { oldPath: null, newPath: null };
}

function newFile(header) {
  const { oldPath, newPath } = parseGitHeader(header);
  return {
    path: newPath || oldPath || '',
    oldPath,
    newPath,
    status: 'modified',
    binary: false,
    additions: 0,
    deletions: 0,
    hunks: [],
    lineCount: 0,
  };
}

/**
 * Parses a unified diff (git format; plain `---`/`+++` patches work too).
 * @param {string} patch
 * @param {{maxLines?: number}} [opts]
 * @returns {{files: Array<{path: string, oldPath: string|null, newPath: string|null,
 *   status: 'added'|'deleted'|'modified'|'renamed'|'copied', binary: boolean, additions: number,
 *   deletions: number, lineCount: number, hunks: Array<{header: string, section: string, oldStart: number,
 *   oldLines: number, newStart: number, newLines: number, lines: Array<{type: 'add'|'del'|'ctx'|'note',
 *   text: string, oldNo: number|null, newNo: number|null}>}>}>, cut: boolean}}
 */
export function parseUnifiedDiff(patch, { maxLines = MAX_PATCH_LINES } = {}) {
  const files = [];
  const text = typeof patch === 'string' ? patch : '';
  if (!text) return { files, cut: false };
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  if (lines.length && lines[lines.length - 1] === '') lines.pop();
  const cut = lines.length > maxLines;
  let file = null;
  let hunk = null;
  let oldLeft = 0;
  let newLeft = 0;
  let oldNo = 0;
  let newNo = 0;

  const startFile = (header) => {
    file = newFile(header);
    files.push(file);
    hunk = null;
    oldLeft = 0;
    newLeft = 0;
  };

  for (let i = 0; i < Math.min(lines.length, maxLines); i++) {
    const line = lines[i];
    const inHunk = hunk && (oldLeft > 0 || newLeft > 0);
    if (inHunk) {
      const c = line[0];
      if (c === '+') {
        hunk.lines.push({ type: 'add', text: line.slice(1), oldNo: null, newNo: newNo++ });
        newLeft -= 1;
        file.additions += 1;
        file.lineCount += 1;
        continue;
      }
      if (c === '-') {
        hunk.lines.push({ type: 'del', text: line.slice(1), oldNo: oldNo++, newNo: null });
        oldLeft -= 1;
        file.deletions += 1;
        file.lineCount += 1;
        continue;
      }
      if (c === ' ' || line === '') {
        hunk.lines.push({ type: 'ctx', text: line.slice(1), oldNo: oldNo++, newNo: newNo++ });
        oldLeft -= 1;
        newLeft -= 1;
        file.lineCount += 1;
        continue;
      }
      if (c === '\\') {
        hunk.lines.push({ type: 'note', text: line.slice(1).trim(), oldNo: null, newNo: null });
        continue;
      }
      // Anything else ends the hunk early (malformed or cut patch): fall through to headers.
      oldLeft = 0;
      newLeft = 0;
    }
    if (line.startsWith('diff --git ')) { startFile(line); continue; }
    if (hunk && line[0] === '\\') {
      hunk.lines.push({ type: 'note', text: line.slice(1).trim(), oldNo: null, newNo: null });
      continue;
    }
    const m = HUNK_RE.exec(line);
    if (m) {
      if (!file) startFile('diff --git');
      oldNo = Number(m[1]);
      newNo = Number(m[3]);
      oldLeft = m[2] === undefined ? 1 : Number(m[2]);
      newLeft = m[4] === undefined ? 1 : Number(m[4]);
      hunk = {
        header: line, section: m[5] || '', oldStart: oldNo, oldLines: oldLeft, newStart: newNo, newLines: newLeft, lines: [],
      };
      file.hunks.push(hunk);
      continue;
    }
    if (line.startsWith('--- ')) {
      if (!file || file.hunks.length) startFile('diff --git');
      const p = stripPrefix(line.slice(4));
      file.oldPath = p;
      if (!file.path && p) file.path = p;
      continue;
    }
    if (line.startsWith('+++ ')) {
      if (!file) startFile('diff --git');
      const p = stripPrefix(line.slice(4));
      file.newPath = p;
      if (p) file.path = p;
      continue;
    }
    if (!file) continue;
    if (line.startsWith('new file mode')) file.status = 'added';
    else if (line.startsWith('deleted file mode')) file.status = 'deleted';
    else if (line.startsWith('rename from ')) { file.status = 'renamed'; file.oldPath = unquotePath(line.slice(12)); }
    else if (line.startsWith('rename to ')) { file.status = 'renamed'; file.newPath = unquotePath(line.slice(10)); file.path = file.newPath; }
    else if (line.startsWith('copy from ')) { file.status = 'copied'; file.oldPath = unquotePath(line.slice(10)); }
    else if (line.startsWith('copy to ')) { file.status = 'copied'; file.newPath = unquotePath(line.slice(8)); file.path = file.newPath; }
    else if (line.startsWith('Binary files ') || line === 'GIT binary patch') file.binary = true;
  }
  for (const f of files) {
    if (f.status === 'modified' && f.oldPath === null && f.newPath) f.status = 'added';
    if (f.status === 'modified' && f.newPath === null && f.oldPath) { f.status = 'deleted'; f.path = f.oldPath; }
  }
  return { files, cut };
}

/**
 * Totals of a parsed diff.
 * @param {{files: object[]}} parsed
 * @returns {{files: number, insertions: number, deletions: number}}
 */
export function diffTotals(parsed) {
  const list = parsed?.files || [];
  return {
    files: list.length,
    insertions: list.reduce((n, f) => n + (f.additions || 0), 0),
    deletions: list.reduce((n, f) => n + (f.deletions || 0), 0),
  };
}

/**
 * Joins the server's file list (`[{path, status}]`, complete even when the patch is cut) with the
 * parsed patch: one entry per file, in the server's order, then parsed files it did not list.
 * @param {Array<{path: string, status: string}>} serverFiles
 * @param {{files: object[]}} parsed
 * @returns {Array<{path: string, status: string, parsed: object|null}>}
 */
export function mergeFileLists(serverFiles, parsed) {
  const byPath = new Map((parsed?.files || []).map((f) => [f.path, f]));
  const out = [];
  const seen = new Set();
  for (const f of Array.isArray(serverFiles) ? serverFiles : []) {
    if (!f || typeof f.path !== 'string' || seen.has(f.path)) continue;
    seen.add(f.path);
    const p = byPath.get(f.path) || null;
    out.push({ path: f.path, status: String(f.status || p?.status || 'modified'), parsed: p });
  }
  for (const p of parsed?.files || []) {
    if (!p.path || seen.has(p.path)) continue;
    seen.add(p.path);
    out.push({ path: p.path, status: p.status, parsed: p });
  }
  return out;
}

/**
 * Splits a path into directory and file name ("web/ui/topbar.js" → {dir: 'web/ui/', name: 'topbar.js'}).
 * @param {string} p
 */
export function splitPath(p) {
  const s = String(p ?? '');
  const i = s.lastIndexOf('/');
  return i < 0 ? { dir: '', name: s } : { dir: s.slice(0, i + 1), name: s.slice(i + 1) };
}
